// Client-side artwork ATTRIBUTE detection for uploaded files. Sibling of
// detectBarcode (barcode presence) and docThumbnail (thumbnail) — same lazy,
// best-effort, never-throws discipline. This module ABSORBED the old
// detectPageCount (now deleted): page count is one of the attributes it reads.
// It reads the print-relevant ATTRIBUTES a customer would otherwise type by
// hand and PRE-FILLS the matching configurator fields for them to confirm:
//
//   - trim size  — the PDF page's trimmed dimensions, matched to a catalog size
//   - color      — does the piece carry meaningful color, or is it B&W? A
//                  manuscript can also read 'mixed' (part color, part B&W).
//   - sides      — best-effort single- vs double-sided (see inferSides caveats)
//   - bleed       — does the art look like it prints to the edge? (HINT ONLY)
//   - gutter      — do the inside pages alternate an inner (spine) margin, the
//                  way a book imposed for binding does? (HINT ONLY, 'guts' scans)
//   - page count — content pages (folds in what detectPageCount used to read)
//
// This is intent-capture ASSISTANCE, NOT pricing math (CLAUDE.md §"Pricing logic
// lives in the ERP"). Every value is a SUGGESTION the customer confirms or
// corrects in the form; nothing here is submitted silently, and SchemaForm
// applies each value only while the field is still empty or holds a value WE
// last filled (never clobbering a customer edit). Bleed is the one exception to
// "pre-fill": business rule R-AW-01 requires the customer to elicit bleed Y/N
// explicitly, so we surface a HINT and never auto-select the bleed field.
//
//   PDF      — opened once via the shared pdf.js loader. Size comes from the
//              page-1 viewport (no raster); color / sides / bleed come from
//              rasterizing a SAMPLE of pages at low resolution and inspecting
//              the pixels. Page count is pdf.js's numPages.
//   PNG/JPEG — decoded to a canvas; color + bleed only (a raster image carries
//              no reliable physical trim, and a lone image isn't a multi-page
//              document, so size / sides / page count are left undetected).
//   Other     — SVG / TIFF / ZIP / AI / EPS aren't reliably rasterizable here,
//              so we return null (no signal → no pre-fill), exactly like the
//              barcode sibling returns 'unknown'.
//
// Returns null for anything we can't read confidently (unsupported type,
// oversized image, render/parse failure) — the caller then leaves every field
// for manual entry. We never assert an attribute we didn't actually measure.

import type { PDFDocumentProxy } from 'pdfjs-dist';

import { loadPdfjs } from './loadPdfjs.js';

// 'mixed' (part color, part black & white) is produced ONLY for a 'guts'
// (manuscript) scan — its interior_color field offers it. Cover/flat scans
// collapse to the binary pick (their color fields don't offer 'mixed'), and a
// lone raster image is one or the other too. See classifyColor.
export type DetectedColor = 'color' | 'black_and_white' | 'mixed';
export type DetectedSides = 'single_sided' | 'double_sided';

// What detection should look for, scoped by the upload's component role. Each
// role inspects a different shape of file: a cover is 1–2 pages (outer face,
// maybe the inside cover), a manuscript ('guts') is a long interior we sample,
// and a flat piece ('general') is a single sheet. Roles not listed here aren't
// auto-filled (endsheet / emboss / foil / variable-data / combined pile).
export type ArtworkScanRole = 'cover' | 'guts' | 'general';

export type ArtworkDetection = {
  filename: string;
  // Total content pages (one image per PDF page). Undefined for raster images.
  page_count?: number;
  // Page-1 trimmed dimensions in inches (width × height, orientation as laid
  // out). Undefined for images (no reliable physical size).
  trim_in?: { width: number; height: number };
  // Color of the inspected pages: 'black_and_white' (no color found), 'color'
  // (every inspected page carries color), or — for a manuscript only — 'mixed'
  // (some pages color, some B&W). Undefined when we couldn't rasterize anything
  // to judge. See classifyColor for the role-aware rule.
  color?: DetectedColor;
  // Best-effort single/double-sided. Undefined when we can't tell (the caller
  // then leaves the field at its default). See inferSides.
  sides?: DetectedSides;
  // HINT ONLY: the art looks like it prints edge-to-edge (content reaches the
  // trim on most inspected pages). Never auto-selects the bleed field — the
  // customer still chooses Y/N explicitly (R-AW-01).
  bleed_hint?: boolean;
  // HINT ONLY ('guts' scans): the inside pages look laid out for binding —
  // consecutive leaves show a wider inner (spine-side) margin that ALTERNATES
  // with page parity (recto vs verso). There is no margin field to set, so this
  // only surfaces a "looks laid out for binding" confirmation; it never fills a
  // field. See detectGutter.
  gutter_hint?: boolean;
  // How many pages we actually rasterized + inspected (so the consumer can say
  // "we spot-checked N of M pages" rather than imply we read the whole book).
  inspected_pages?: number;
};

// ── tunables ──────────────────────────────────────────────────────────────
// Longest-side render target for the analysis raster, in px. Color / blank /
// edge inspection don't need the resolution a barcode scan does (PDF_SCAN_MAX_PX
// is 3500) — a small raster samples plenty of pixels and keeps per-page cost
// trivial even when we inspect a dozen pages of a long manuscript.
const ANALYZE_MAX_PX = 240;

// A pixel "carries color" when its chroma (max channel − min channel) clears
// this 0–255 threshold. Black text, grey rules, and near-white paper all have
// near-zero chroma, so they don't count; only genuinely saturated ink does. Set
// high enough to shrug off JPEG fringing and anti-aliasing on black text.
const CHROMA_THRESHOLD = 28;
// A page is "color" when at least this fraction of its pixels carry color —
// small, so a logo or a single color photo flips it, but not one stray pixel.
const COLOR_PAGE_FRACTION = 0.012;

// A pixel is "ink" (non-white) when any channel sits this far below pure white.
const WHITE_SLACK = 12;
// A page is "blank" when under this fraction of pixels are ink — tolerant of a
// stray page number / running head on an otherwise-empty back, but well below a
// real content page. Drives the single-sided (blank-back) heuristic.
const BLANK_INK_FRACTION = 0.004;

// The art "bleeds" on a page when this fraction of its outer-ring pixels are
// ink — a full-bleed photo lights up the whole ring; a bordered design leaves
// it white. Width of the inspected ring, as a fraction of the page's short side.
const EDGE_RING_FRACTION = 0.03;
const EDGE_BLEED_FRACTION = 0.5;
// The piece gets a bleed HINT when at least this fraction of inspected pages
// reach the edge — for a 1-page cover/flat that's just "does it bleed?"; for a
// multi-page interior it asks the majority to bleed before nudging.
const BLEED_PAGE_FRACTION = 0.5;

// Content "reaches an edge" (corroborates bleed) when its inset from that edge
// is under this fraction of the page. A pair of OPPOSITE edges both flush
// (top+bottom or left+right) reads as edge-to-edge even when the outer-ring
// average sits just under EDGE_BLEED_FRACTION — e.g. a full-bleed photo with a
// light center. Reinforces the ring test; never the sole basis on its own edge.
const EDGE_FLUSH_FRACTION = 0.012;
// A column/row counts toward the content box (which sets the per-edge margins)
// only when this fraction of its pixels are ink — high enough to ignore a stray
// speck or a lone page-number folio, low enough to catch a real text column.
const MARGIN_LINE_INK_FRACTION = 0.02;
// The inside pages read as "imposed for binding" when a leaf's inner (spine-side)
// margin is at least this much wider than its outer one (fraction of page width),
// and the wide side ALTERNATES with page parity. Below this, the asymmetry is
// noise (centered text, justified blocks) and yields no gutter hint.
const GUTTER_MIN_DELTA = 0.04;

// Skip decoding a raster image past this size — a very large bitmap risks an
// OOM stall for little gain. Mirrors the thumbnail / barcode source caps. PDFs
// are NOT capped here: page count + size are cheap and worth reading even on a
// big manuscript (the old detectPageCount had no cap), and we only ever
// rasterize a SAMPLE of pages, one low-res canvas at a time.
const MAX_IMAGE_BYTES = 25 * 1024 * 1024;

function isPdf(content_type: string, filename: string): boolean {
  return content_type === 'application/pdf' || filename.toLowerCase().endsWith('.pdf');
}

function isRasterImage(content_type: string, filename: string): boolean {
  const name = filename.toLowerCase();
  return (
    content_type === 'image/png' ||
    content_type === 'image/jpeg' ||
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg')
  );
}

// Per-page pixel analysis from a decoded surface. Margins are the content box's
// inset from each edge as a fraction of the page (0 = ink runs to that edge;
// 0.5 = no content found / blank). marginLeft/Right drive the gutter read;
// all four corroborate bleed (opposite edges both flush).
type PageAnalysis = {
  isBlank: boolean;
  isColor: boolean;
  bleeds: boolean;
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
};

function analyzeImageData(img: ImageData): PageAnalysis {
  const { data, width, height } = img;
  const total = width * height;
  if (total === 0) {
    return {
      isBlank: true,
      isColor: false,
      bleeds: false,
      marginLeft: 0.5,
      marginRight: 0.5,
      marginTop: 0.5,
      marginBottom: 0.5,
    };
  }

  // Single pass over every pixel: tally ink + color, build per-column / per-row
  // ink histograms (for the content box → margins), and measure the outer ring
  // (for bleed). One loop with x/y in hand replaces the old ink loop + separate
  // ring loop — the margins need x/y anyway, so the ring rides along for free.
  let inkPixels = 0;
  let colorPixels = 0;
  const colInk = new Uint32Array(width);
  const rowInk = new Uint32Array(height);
  const ring = Math.max(1, Math.round(Math.min(width, height) * EDGE_RING_FRACTION));
  let ringPixels = 0;
  let ringInk = 0;
  for (let y = 0; y < height; y++) {
    const onVerticalEdge = y < ring || y >= height - ring;
    const rowBase = y * width;
    for (let x = 0; x < width; x++) {
      const onRing = onVerticalEdge || x < ring || x >= width - ring;
      if (onRing) ringPixels++;
      const idx = (rowBase + x) * 4;
      // Treat fully transparent as white paper (the analysis canvas is painted
      // white first, so this rarely fires — but a directly-decoded PNG with
      // transparency reaches here).
      const a = data[idx + 3] ?? 0;
      if (a === 0) continue;
      const r = data[idx] ?? 0;
      const g = data[idx + 1] ?? 0;
      const b = data[idx + 2] ?? 0;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      if (max < 255 - WHITE_SLACK || min < 255 - WHITE_SLACK) {
        inkPixels++;
        // x ∈ [0,width), y ∈ [0,height) — always in-bounds; the `?? 0` only
        // satisfies noUncheckedIndexedAccess (TypedArray reads widen to number|undefined).
        colInk[x] = (colInk[x] ?? 0) + 1;
        rowInk[y] = (rowInk[y] ?? 0) + 1;
        if (onRing) ringInk++;
      }
      if (max - min > CHROMA_THRESHOLD) colorPixels++;
    }
  }
  const isBlank = inkPixels / total < BLANK_INK_FRACTION;
  const isColor = colorPixels / total > COLOR_PAGE_FRACTION;

  // Content box → per-edge margins. A column/row joins the box only when its ink
  // clears MARGIN_LINE_INK_FRACTION of its length, so a speck or a lone folio
  // doesn't define the box. No qualifying line ⇒ 0.5 margins (no signal).
  const colMin = Math.max(1, Math.round(height * MARGIN_LINE_INK_FRACTION));
  const rowMin = Math.max(1, Math.round(width * MARGIN_LINE_INK_FRACTION));
  let firstCol = -1;
  let lastCol = -1;
  let firstRow = -1;
  let lastRow = -1;
  for (let x = 0; x < width; x++) {
    if ((colInk[x] ?? 0) >= colMin) {
      if (firstCol < 0) firstCol = x;
      lastCol = x;
    }
  }
  for (let y = 0; y < height; y++) {
    if ((rowInk[y] ?? 0) >= rowMin) {
      if (firstRow < 0) firstRow = y;
      lastRow = y;
    }
  }
  const hasBox = firstCol >= 0 && firstRow >= 0;
  const marginLeft = hasBox ? firstCol / width : 0.5;
  const marginRight = hasBox ? (width - 1 - lastCol) / width : 0.5;
  const marginTop = hasBox ? firstRow / height : 0.5;
  const marginBottom = hasBox ? (height - 1 - lastRow) / height : 0.5;

  // Bleed: the outer ring is mostly ink (a full-bleed photo lights up the whole
  // band; a bordered design leaves it white) OR the content box runs flush to a
  // PAIR of opposite edges (top+bottom or left+right) — the latter rescues a
  // full-bleed image whose lighter center pulls the ring average under the bar.
  const ringBleed = ringPixels > 0 && ringInk / ringPixels > EDGE_BLEED_FRACTION;
  const flushBleed =
    hasBox &&
    ((marginTop < EDGE_FLUSH_FRACTION && marginBottom < EDGE_FLUSH_FRACTION) ||
      (marginLeft < EDGE_FLUSH_FRACTION && marginRight < EDGE_FLUSH_FRACTION));
  const bleeds = ringBleed || flushBleed;

  return { isBlank, isColor, bleeds, marginLeft, marginRight, marginTop, marginBottom };
}

// Render one PDF page to a small white-backed canvas and analyze its pixels.
// Returns null when the page can't be rendered (no 2D context, surface limit) —
// the caller treats a missing page as "not inspected" rather than guessing.
async function analyzePdfPage(
  doc: PDFDocumentProxy,
  pageNumber: number,
): Promise<PageAnalysis | null> {
  const page = await doc.getPage(pageNumber);
  const unscaled = page.getViewport({ scale: 1 });
  const scale = Math.min(ANALYZE_MAX_PX / Math.max(unscaled.width, unscaled.height), 1);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(viewport.width));
  canvas.height = Math.max(1, Math.ceil(viewport.height));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  // White backing so a transparent page reads as a sheet of paper (matches the
  // thumbnail + barcode rasterizers).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return analyzeImageData(ctx.getImageData(0, 0, canvas.width, canvas.height));
}

// Which page numbers (1-based) to rasterize + inspect for a given role, capped
// so a long manuscript never rasterizes hundreds of pages.
//   cover   — pages 1–2 (outer face + inside front, or a one-page wrap).
//   general — page 1 (a flat sheet; a back, if present, is page 2).
//   guts    — pages 1–4 (the leading leaves, so the blank-back single-sided
//             pattern is visible) plus an even spread across the rest, so a
//             color page deep in an otherwise-B&W book still has a chance to be
//             sampled. Capped at MAX_GUTS_INSPECT pages total.
const MAX_GUTS_INSPECT = 16;
function pickInspectionPages(total: number, role: ArtworkScanRole): number[] {
  if (total <= 0) return [];
  if (role === 'cover') return rangeUpTo(2, total);
  if (role === 'general') return rangeUpTo(2, total);
  // guts: leading leaves + an even spread.
  const set = new Set<number>([1, 2, 3, 4].filter((n) => n <= total));
  const remaining = MAX_GUTS_INSPECT - set.size;
  if (remaining > 0 && total > 4) {
    const step = Math.max(1, Math.floor(total / remaining));
    for (let n = 5; n <= total && set.size < MAX_GUTS_INSPECT; n += step) set.add(n);
  }
  return Array.from(set).sort((a, b) => a - b);
}

function rangeUpTo(n: number, total: number): number[] {
  const out: number[] = [];
  for (let i = 1; i <= Math.min(n, total); i++) out.push(i);
  return out;
}

// Best-effort single/double-sided inference.
//   cover / general — a press piece's sided-ness tracks its page count: a
//     one-page cover prints the outside only (single-sided); a two-page cover
//     adds the inside (double-sided). Same for a flat sheet (front only vs
//     front + back). This is a SUGGESTION the customer freely overrides.
//   guts — a content PDF doesn't encode sided-ness (it's a press choice), so we
//     only flag single-sided when the file is laid out that way: content fronts
//     with BLANK backs (pages 2 & 4 blank while 1 & 3 carry content). Anything
//     else returns undefined and the field keeps its double-sided default —
//     books are double-sided far more often than not, so we don't assert it.
function inferSides(
  role: ArtworkScanRole,
  total: number,
  blankByPage: Map<number, boolean>,
): DetectedSides | undefined {
  if (role === 'cover' || role === 'general') {
    if (total >= 2) return 'double_sided';
    if (total === 1) return 'single_sided';
    return undefined;
  }
  // guts blank-back pattern — needs at least the first four leaves inspected.
  const p1 = blankByPage.get(1);
  const p2 = blankByPage.get(2);
  const p3 = blankByPage.get(3);
  const p4 = blankByPage.get(4);
  if (p1 === undefined || p2 === undefined || p3 === undefined || p4 === undefined) return undefined;
  if (total < 4) return undefined;
  if (!p1 && p2 && !p3 && p4) return 'single_sided';
  return undefined;
}

// Role-aware color classification from a sample of inspected pages.
//   no color page              → 'black_and_white'
//   every inspected page color → 'color'
//   some but not all (guts only) → 'mixed'  (part color, part B&W body)
// 'mixed' is produced ONLY for a manuscript ('guts'): a 1–2-page cover or a flat
// sheet is one or the other, and their color fields don't offer 'mixed', so we
// never pre-fill an option they can't take — any color on those collapses to
// 'color'. Like every detected value, this is a SUGGESTION the customer confirms.
function classifyColor(role: ArtworkScanRole, colorPages: number, inspected: number): DetectedColor {
  if (colorPages === 0) return 'black_and_white';
  if (role !== 'guts') return 'color';
  return colorPages >= inspected ? 'color' : 'mixed';
}

// The binding-gutter signature of a book interior imposed for print: each leaf
// reserves extra space on the SPINE side, so its inner margin is wider than its
// outer one — and because facing pages mirror, the wide side ALTERNATES with page
// parity (a recto's gutter is on the left, a verso's on the right, or the mirror
// of that). We read it off the leading consecutive leaves (pages 1–4, where
// imposition is most uniform — a deep chapter-opener can carry its own margins),
// using each page's inner/outer asymmetry (left − right). A hint is reported only
// when we actually SAW the flip: at least one odd and one even page each clear
// GUTTER_MIN_DELTA, every odd page agrees in sign, every even page agrees, and
// the two parities are opposite. Anything ragged (pages disagree within a parity,
// or both parities lean the same way) returns false — we don't assert a gutter we
// can't see cleanly. HINT only: there is no margin field to set.
function detectGutter(marginsByPage: Map<number, { left: number; right: number }>): boolean {
  let oddSign = 0; // sign of (left − right) on odd pages (recto)
  let evenSign = 0; // ... on even pages (verso)
  let oddSeen = false;
  let evenSeen = false;
  for (const [page, m] of marginsByPage) {
    if (page > 4) continue; // leading leaves only — most reliably uniform
    const delta = m.left - m.right;
    if (Math.abs(delta) < GUTTER_MIN_DELTA) continue; // roughly symmetric — no signal
    const sign = delta > 0 ? 1 : -1;
    if (page % 2 === 1) {
      if (oddSeen && oddSign !== sign) return false; // odd pages disagree
      oddSign = sign;
      oddSeen = true;
    } else {
      if (evenSeen && evenSign !== sign) return false; // even pages disagree
      evenSign = sign;
      evenSeen = true;
    }
  }
  return oddSeen && evenSeen && oddSign === -evenSign;
}

async function detectPdf(blob: Blob, filename: string, role: ArtworkScanRole): Promise<ArtworkDetection> {
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await blob.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  try {
    const total: number = doc.numPages;
    const out: ArtworkDetection = { filename, page_count: total };

    // Size: page-1 viewport at scale 1 is in PDF points (72 per inch).
    const page1 = await doc.getPage(1);
    const vp = page1.getViewport({ scale: 1 });
    const width = vp.width / 72;
    const height = vp.height / 72;
    if (width > 0 && height > 0) out.trim_in = { width, height };

    // Color / sides / bleed / gutter from a sample of rasterized pages.
    const pages = pickInspectionPages(total, role);
    const blankByPage = new Map<number, boolean>();
    // Inner/outer margins of the non-blank leaves, for the gutter-alternation read.
    const marginsByPage = new Map<number, { left: number; right: number }>();
    let inspected = 0;
    let colorPages = 0;
    let bleedVotes = 0;
    for (const n of pages) {
      const a = await analyzePdfPage(doc, n);
      if (!a) continue;
      inspected++;
      if (a.isColor) colorPages++;
      if (a.bleeds) bleedVotes++;
      blankByPage.set(n, a.isBlank);
      // A blank back carries no content box, so its 0.5 margins would be noise —
      // only feed real pages to the gutter read.
      if (!a.isBlank) marginsByPage.set(n, { left: a.marginLeft, right: a.marginRight });
    }
    out.inspected_pages = inspected;
    if (inspected > 0) {
      out.color = classifyColor(role, colorPages, inspected);
      out.bleed_hint = bleedVotes / inspected >= BLEED_PAGE_FRACTION;
    }
    out.sides = inferSides(role, total, blankByPage);
    // Gutter is a manuscript ('guts') signal only — a 1–2-page cover or a flat
    // sheet has no facing-page alternation to read.
    if (role === 'guts' && detectGutter(marginsByPage)) out.gutter_hint = true;
    return out;
  } finally {
    await doc.destroy();
  }
}

async function detectImage(blob: Blob, filename: string): Promise<ArtworkDetection | null> {
  // createImageBitmap decodes off the main thread; draw to a small canvas so
  // analysis samples a modest pixel count regardless of the source resolution.
  const bitmap = await createImageBitmap(blob);
  try {
    const scale = Math.min(ANALYZE_MAX_PX / Math.max(bitmap.width, bitmap.height), 1);
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    const a = analyzeImageData(ctx.getImageData(0, 0, w, h));
    // Images carry no reliable physical trim and aren't a multi-page document,
    // so we report color + bleed only — size / sides / page count stay manual.
    return { filename, color: a.isColor ? 'color' : 'black_and_white', bleed_hint: a.bleeds, inspected_pages: 1 };
  } finally {
    bitmap.close();
  }
}

// Inspect a file's bytes and return the print attributes we can read, or null
// for anything we can't read confidently. Never throws.
export async function detectArtworkInFile(file: File, role: ArtworkScanRole): Promise<ArtworkDetection | null> {
  try {
    if (isPdf(file.type, file.name)) return await detectPdf(file, file.name, role);
    if (isRasterImage(file.type, file.name)) {
      if (file.size > MAX_IMAGE_BYTES) return null;
      return await detectImage(file, file.name);
    }
    // SVG / TIFF / ZIP / AI / EPS / unknown — not inspectable here.
    return null;
  } catch (err) {
    // Warn (not silent) so a pdf.js parse / image-decode failure is diagnosable
    // rather than looking like "auto-fill just didn't happen".
    console.warn(
      `[artwork-detect] couldn't read attributes from "${file.name}" — leaving those fields for manual entry.`,
      err,
    );
    return null;
  }
}
